import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Zap, AlertTriangle, Clock, User, MessageSquare, TrendingUp, CheckCircle, Edit } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function TicketDetail360() {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [slaStatus, setSlaStatus] = useState({});
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [newAssignment, setNewAssignment] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [escalationReason, setEscalationReason] = useState('');
  const [analyzingAI, setAnalyzingAI] = useState(false);

  useEffect(() => {
    fetchTicketDetail();
  }, [ticketId]);

  const fetchTicketDetail = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/tickets/${ticketId}`);
      setTicket(res.data.ticket);
      setComments(res.data.comments || []);
      setSlaStatus(res.data.sla_status || {});
      setNewStatus(res.data.ticket.status);
    } catch (err) {
      toast.error('Failed to load ticket details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    setPosting(true);
    try {
      await api.post(`/admin/tickets/${ticketId}/comments`, {
        content: newComment,
        is_internal: false
      });
      toast.success('Comment added');
      setNewComment('');
      fetchTicketDetail();
    } catch (err) {
      toast.error('Failed to add comment');
    } finally {
      setPosting(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }

    try {
      await api.put(`/admin/tickets/${ticketId}`, {
        status: newStatus,
        resolution_notes: resolutionNotes || undefined
      });
      toast.success('Ticket status updated');
      setShowStatusDialog(false);
      setResolutionNotes('');
      fetchTicketDetail();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateAssignment = async () => {
    if (!newAssignment) {
      toast.error('Please select an assignment');
      return;
    }

    try {
      await api.put(`/admin/tickets/${ticketId}`, {
        assigned_to: newAssignment
      });
      toast.success('Ticket assignment updated');
      setShowAssignDialog(false);
      fetchTicketDetail();
    } catch (err) {
      toast.error('Failed to update assignment');
    }
  };

  const handleEscalate = async () => {
    if (!escalationReason.trim()) {
      toast.error('Please provide escalation reason');
      return;
    }

    try {
      await api.post(`/admin/tickets/${ticketId}/escalate`, {
        reason: escalationReason,
        escalation_level: (ticket?.escalation_level || 0) + 1
      });
      toast.success('Ticket escalated successfully');
      setShowEscalateDialog(false);
      setEscalationReason('');
      fetchTicketDetail();
    } catch (err) {
      toast.error('Failed to escalate ticket');
    }
  };

  const handleAIAnalysis = async () => {
    setAnalyzingAI(true);
    try {
      const response = await api.post(`/admin/tickets/${ticketId}/ai-analyze`);
      const data = response.data;
      
      if (data.auto_resolved) {
        toast.success(`AI analyzed and auto-resolved! (Confidence: ${data.confidence}%)`);
      } else {
        toast.success(`AI responded with ${data.confidence}% confidence. ${data.reasoning}`);
      }
      
      // Refresh to see AI response
      setTimeout(() => {
        fetchTicketDetail();
      }, 1000);
    } catch (err) {
      toast.error('AI analysis failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setAnalyzingAI(false);
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      critical: 'bg-rose-50 text-rose-800 border-rose-200',
      high: 'bg-orange-50 text-orange-800 border-orange-200',
      medium: 'bg-blue-50 text-blue-800 border-blue-200',
      low: 'bg-slate-50 text-slate-800 border-slate-200'
    };
    return colors[priority] || colors.medium;
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-blue-50 text-blue-800 border-blue-200',
      in_progress: 'bg-amber-50 text-amber-800 border-amber-200',
      pending: 'bg-purple-50 text-purple-800 border-purple-200',
      resolved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
      closed: 'bg-slate-50 text-slate-800 border-slate-200'
    };
    return colors[status] || colors.open;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-muted-foreground">Ticket not found</p>
          <Button onClick={() => navigate('/admin/tickets')} className="mt-4">
            Back to Tickets
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/admin/tickets')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold" data-font="display">Ticket #{ticket.ticket_number}</h1>
              {ticket.ai_resolved && (
                <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200">
                  <Zap className="h-3 w-3 mr-1" />
                  AI Resolved
                </Badge>
              )}
              {ticket.is_internal && (
                <Badge variant="outline" className="bg-purple-50 text-purple-800 border-purple-200">
                  Internal
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground">{ticket.title}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant="outline" className={`${getPriorityColor(ticket.priority)} border`}>
            {ticket.priority.toUpperCase()}
          </Badge>
          <Badge variant="outline" className={`${getStatusColor(ticket.status)} border`}>
            {ticket.status.replace('_', ' ').toUpperCase()}
          </Badge>
          <Button variant="outline" size="sm" onClick={() => setShowStatusDialog(true)}>
            <Edit className="h-4 w-4 mr-2" />
            Update Status
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setNewAssignment(ticket.assigned_to || ''); setShowAssignDialog(true); }}>
            <User className="h-4 w-4 mr-2" />
            Assign Ticket
          </Button>
          {!ticket.is_internal && (ticket.status === 'open' || ticket.status === 'pending') && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleAIAnalysis}
              disabled={analyzingAI}
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
            >
              <Zap className="h-4 w-4 mr-2" />
              {analyzingAI ? 'Analyzing...' : 'Analyze with AI'}
            </Button>
          )}
          {ticket.status !== 'resolved' && ticket.status !== 'closed' && (
            <Button variant="outline" size="sm" onClick={() => setShowEscalateDialog(true)}>
              <TrendingUp className="h-4 w-4 mr-2" />
              Escalate
            </Button>
          )}
        </div>
      </div>

      {/* SLA Warning */}
      {(slaStatus.response_breached || slaStatus.resolution_breached) && (
        <Card className="border-rose-200 bg-rose-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-rose-800">
              <AlertTriangle className="h-5 w-5" />
              <div>
                <strong>SLA Breach Alert:</strong>
                {slaStatus.response_breached && ' Response time exceeded.'}
                {slaStatus.resolution_breached && ' Resolution time exceeded.'}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Left Column - Ticket Details */}
        <div className="col-span-2 space-y-6">
          {/* Description */}
          <Card>
            <CardHeader>
              <CardTitle>Ticket Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{ticket.description}</p>
            </CardContent>
          </Card>

          {/* Comments Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Comments & Responses ({comments.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {comments.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No comments yet</p>
              ) : (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div 
                      key={comment.id} 
                      className={`p-4 rounded-lg border ${
                        comment.is_ai_response 
                          ? 'bg-amber-50 border-amber-200' 
                          : comment.is_internal
                          ? 'bg-purple-50 border-purple-200'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {comment.is_ai_response ? (
                            <>
                              <Zap className="h-4 w-4 text-amber-600" />
                              <strong className="text-amber-800">{comment.author_name}</strong>
                              {comment.ai_confidence && (
                                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">
                                  Confidence: {comment.ai_confidence}%
                                </Badge>
                              )}
                            </>
                          ) : (
                            <>
                              <User className="h-4 w-4" />
                              <strong>{comment.author_name}</strong>
                            </>
                          )}
                          {comment.is_internal && (
                            <Badge variant="outline" className="bg-purple-100 text-purple-800 text-xs">
                              Internal
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="whitespace-pre-wrap text-sm">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Comment Form */}
              <div className="pt-4 border-t">
                <Label htmlFor="new-comment">Add Comment</Label>
                <Textarea
                  id="new-comment"
                  placeholder="Enter your comment..."
                  rows={3}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="mt-2"
                />
                <Button 
                  onClick={handleAddComment} 
                  disabled={posting || !newComment.trim()}
                  className="mt-2"
                >
                  {posting ? 'Posting...' : 'Add Comment'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Metadata */}
        <div className="space-y-6">
          {/* Ticket Info */}
          <Card>
            <CardHeader>
              <CardTitle>Ticket Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <span className="text-muted-foreground">Department:</span>
                <p className="font-medium capitalize">{ticket.department}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Assigned To:</span>
                <p className="font-medium">{ticket.assigned_to}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Created By:</span>
                <p className="font-medium">{ticket.created_by_name}</p>
                {ticket.created_by_email && (
                  <p className="text-xs text-muted-foreground">{ticket.created_by_email}</p>
                )}
              </div>
              <div>
                <span className="text-muted-foreground">Created At:</span>
                <p className="font-medium">{new Date(ticket.created_at).toLocaleString()}</p>
              </div>
              {ticket.resolved_at && (
                <div>
                  <span className="text-muted-foreground">Resolved At:</span>
                  <p className="font-medium">{new Date(ticket.resolved_at).toLocaleString()}</p>
                </div>
              )}
              {ticket.escalated && (
                <div>
                  <span className="text-muted-foreground">Escalation Level:</span>
                  <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200">
                    Level {ticket.escalation_level}
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>

          {/* SLA Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                SLA Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <span className="text-muted-foreground">Response Due:</span>
                <p className={`font-medium ${slaStatus.response_breached ? 'text-rose-600' : ''}`}>
                  {ticket.sla_response_due ? new Date(ticket.sla_response_due).toLocaleString() : 'Not set'}
                </p>
                {ticket.first_response_at && (
                  <p className="text-xs text-emerald-600">
                    <CheckCircle className="h-3 w-3 inline mr-1" />
                    Responded
                  </p>
                )}
              </div>
              <div>
                <span className="text-muted-foreground">Resolution Due:</span>
                <p className={`font-medium ${slaStatus.resolution_breached ? 'text-rose-600' : ''}`}>
                  {ticket.sla_resolution_due ? new Date(ticket.sla_resolution_due).toLocaleString() : 'Not set'}
                </p>
                {ticket.resolved_at && (
                  <p className="text-xs text-emerald-600">
                    <CheckCircle className="h-3 w-3 inline mr-1" />
                    Resolved
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* AI Info (if applicable) */}
          {ticket.ai_confidence && (
            <Card className="border-amber-200 bg-amber-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <Zap className="h-5 w-5" />
                  AI Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Confidence Score:</span>
                  <p className="font-bold text-lg text-amber-800">{ticket.ai_confidence}%</p>
                </div>
                {ticket.ai_resolved && (
                  <div className="pt-2 border-t border-amber-200">
                    <Badge className="bg-amber-600 text-white">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Auto-Resolved by AI
                    </Badge>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Update Status Dialog */}
      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Ticket Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-status">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger id="new-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(newStatus === 'resolved' || newStatus === 'closed') && (
              <div className="space-y-2">
                <Label htmlFor="resolution-notes">Resolution Notes</Label>
                <Textarea
                  id="resolution-notes"
                  placeholder="Describe how this was resolved..."
                  rows={3}
                  value={resolutionNotes}
                  onChange={(e) => setResolutionNotes(e.target.value)}
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStatusDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateStatus}>Update Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Escalate Dialog */}
      <Dialog open={showEscalateDialog} onOpenChange={setShowEscalateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Escalate Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Escalating to Level {(ticket?.escalation_level || 0) + 1}
            </p>
            <div className="space-y-2">
              <Label htmlFor="escalation-reason">Escalation Reason *</Label>
              <Textarea
                id="escalation-reason"
                placeholder="Explain why this ticket needs escalation..."
                rows={4}
                value={escalationReason}
                onChange={(e) => setEscalationReason(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEscalateDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEscalate} disabled={!escalationReason.trim()}>
              Escalate Ticket
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="assignment">Assign To</Label>
              <Select value={newAssignment} onValueChange={setNewAssignment}>
                <SelectTrigger id="assignment">
                  <SelectValue placeholder="Select team or agent" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="customer-success-team">Customer Success Team</SelectItem>
                  <SelectItem value="driver-support-team">Driver Support Team</SelectItem>
                  <SelectItem value="vendor-relations-team">Vendor Relations Team</SelectItem>
                  <SelectItem value="technical-support-team">Technical Support Team</SelectItem>
                  <SelectItem value="billing-team">Billing Team</SelectItem>
                  <SelectItem value="escalation-team">Escalation Team</SelectItem>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">
                Current assignment: <span className="font-medium">{ticket?.assigned_to || 'Unassigned'}</span>
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateAssignment}>Update Assignment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
